import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';

const priorities = [
  {
    tag: 'Priority Area 1',
    title: 'Cognitive Decline & Dementia',
    body: 'Regular chess play provides structured cognitive stimulation — engaging memory, planning, pattern recognition, and executive function simultaneously. A growing body of peer-reviewed research supports chess as a viable intervention for cognitive health in older adults.',
    cite: 'A 12-week chess programme improved cognition and quality of life in institutionalised older adults — Geriatric Nursing, 2021. Chess training identified as a viable intervention to improve cognitive fitness — Medical Research Archives, 2023.',
  },
  {
    tag: 'Priority Area 2',
    title: 'Social Isolation & Loneliness',
    body: 'Chess is inherently social — it requires a partner, encourages conversation, and creates community. For older adults and isolated individuals, a chess group provides structured, purposeful social contact aligned with the government’s loneliness strategy.',
    cite: 'Group-based activities providing structured social contact are identified as a primary intervention in the government’s national loneliness strategy.',
  },
  {
    tag: 'Priority Area 3',
    title: 'Mental Health & Anxiety',
    body: 'Chess provides structured cognitive engagement — a form of active mindfulness — shown to reduce anxiety and improve mood. It offers a sense of agency, progress, and achievement accessible to people across a wide range of mental health presentations.',
    cite: 'Screen-free, face-to-face, structured cognitive activity — recommended by NICE as part of a holistic approach to mild-to-moderate anxiety and low mood.',
  },
  {
    tag: 'Priority Area 4',
    title: 'Children & Young People’s Mental Health',
    body: 'Chess builds patience, resilience, and self-regulation — skills directly relevant to emotional wellbeing. Chess clubs in schools and community settings offer a consistent, supportive social environment for young people who may be struggling.',
    cite: 'Chess provides a constructive, screen-free activity for young people building the emotional regulation skills identified in the NHS’s children and young people’s mental health strategy.',
  },
];

const evidence = [
  ['g=0.38', 'Mathematical achievement', 'Moderate positive effect on maths performance — including logical reasoning, pattern recognition, and systematic problem-solving.'],
  ['g=0.34', 'Cognitive ability', 'Overall cognitive ability — including executive function, attention, and working memory — improves with sustained chess instruction.'],
  ['24', 'Studies in meta-analysis', 'Peer-reviewed meta-analysis across 24 studies and 5,221 children. Benefits increase with programme duration — a 25–30 hour annual programme shows the strongest gains.'],
];

const nhsFunding = [
  ['NASP Community Fund', 'National Academy for Social Prescribing periodically opens community funding rounds — Chess for Wellbeing is directly eligible'],
  ['Primary Care Network budgets', 'PCNs have budgets for social prescribing link workers and community activities — most direct route to NHS-linked delivery'],
  ['Integrated Care Board grants', 'ICBs commission community health and wellbeing services — chess targeting cognitive health or loneliness aligns with typical ICB priorities'],
  ['UK Shared Prosperity Fund', 'Available via local authorities for community wellbeing and skills programmes — particularly relevant in areas of deprivation'],
];

const schoolFunding = [
  ['Primary PE & Sport Premium', 'Ring-fenced school funding for physical and enrichment activities — chess is increasingly accepted as eligible'],
  ['Pupil Premium', 'Schools can use Pupil Premium for enrichment activities improving outcomes for disadvantaged pupils — chess has a strong evidence base here'],
  ['DfE Enrichment Fund', '£22.5m over three years for 400 schools in the most deprived areas — Chess for Schools is directly eligible'],
  ['Chess in Schools & Communities', 'The national charity CSC part-funds chess programmes in schools in partnership with local coaches — Robert is an accredited CSC coach'],
];

export default function Wellbeing() {
  return (
    <>
      <PageHero
        tag="Chess for Wellbeing"
        title={<>Chess as a <em>community intervention</em></>}
        lede="A structured, evidence-informed chess programme for NHS social prescribing, schools enrichment, and community health — addressing cognitive decline, social isolation, and educational attainment simultaneously."
      />

      <section className="nhs">
        <div className="inner">
          <Reveal>
            <p className="section-tag">NHS &amp; Social Prescribing</p>
            <h2 className="section-title">Chess as a <em>social prescription</em></h2>
            <div className="divider" />
            <p className="section-body">
              Chess for Wellbeing is a referrable social prescribing activity designed for GP surgeries,
              Primary Care Networks, community centres, care homes, and libraries — addressing four of
              the NHS&rsquo;s highest-priority health challenges in a single, low-cost community intervention.
            </p>
            <div className="policy-note">
              <p className="pn-label">Policy Alignment</p>
              <p>
                Chess for Wellbeing aligns with the NHS Long Term Plan (social prescribing as a core
                pillar), the National Academy for Social Prescribing&rsquo;s community fund, the Welsh
                Government&rsquo;s National Framework for Social Prescribing, and NICE guidelines on
                non-pharmacological interventions for cognitive decline.
              </p>
            </div>
          </Reveal>

          <div className="health-grid">
            {priorities.map((p, i) => (
              <Reveal key={p.tag} className="health-card" delay={(i % 2) * 110}>
                <p className="health-tag">{p.tag}</p>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
                <div className="evidence-cite">{p.cite}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="schools">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Education</p>
            <h2 className="section-title">Chess in <em>schools</em></h2>
            <div className="divider" />
            <p className="section-body">
              Chess has one of the most studied evidence bases of any extracurricular educational
              activity. A peer-reviewed meta-analysis across 24 studies and nearly 5,000 children found
              meaningful positive effects on mathematical ability, cognitive skills, and literacy — with
              benefits increasing with programme duration.
            </p>
          </Reveal>
          <div className="evidence-grid">
            {evidence.map(([num, h, p], i) => (
              <Reveal key={h} className="ev-card" delay={i * 90}>
                <div className="ev-num">{num}</div>
                <h4>{h}</h4>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="dfe-note">
            <p className="dfe-label">DfE Policy Alignment</p>
            <p>
              Chess for Schools aligns with the DfE&rsquo;s new enrichment benchmarks (schools now
              assessed by Ofsted on enrichment provision), the Primary PE and Sport Premium, Pupil
              Premium enrichment objectives for disadvantaged pupils, and the £22.5 million DfE
              enrichment fund for schools in deprived areas. CSC accreditation also provides access to
              Chess in Schools &amp; Communities funding and school network partnerships.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="funding">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Funding Routes</p>
            <h2 className="section-title">Active funding <em>streams</em></h2>
            <div className="divider" />
            <p className="section-body">
              Multiple live funding streams are available for Chess for Wellbeing programmes in both the
              NHS and education sectors. Chess at Sea is actively pursuing these routes.
            </p>
          </Reveal>
          <div className="funding-cols">
            <Reveal className="fund-col">
              <h3>NHS &amp; Social Prescribing</h3>
              <ul className="fund-list">
                {nhsFunding.map(([s, t]) => (
                  <li className="fund-item" key={s}><strong>{s}</strong><span>{t}</span></li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="fund-col" delay={120}>
              <h3>Schools &amp; Education</h3>
              <ul className="fund-list">
                {schoolFunding.map(([s, t]) => (
                  <li className="fund-item" key={s}><strong>{s}</strong><span>{t}</span></li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="cta-band">
        <Reveal>
          <h2>Interested in <em>Chess for Wellbeing?</em></h2>
          <p>
            Whether you are a commissioner, a GP surgery, a school, or a community organisation —
            Robert would welcome a conversation about how a chess programme could work in your setting.
          </p>
          <Link to="/about#contact" className="btn-primary">Get in Touch <span className="arrow">→</span></Link>
        </Reveal>
      </section>
    </>
  );
}
